import { useState, useEffect } from "react";
import { useAuth, API, authFetch } from "@/App";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Zap, Mail, FileSpreadsheet, History as HistoryIcon, LogOut, 
  CheckCircle, AlertCircle, Clock, Eye, Download, Loader2, Shield
} from "lucide-react";

// Sidebar Component (same as Dashboard)
const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { icon: FileSpreadsheet, label: "Tableau", path: "/tableau" },
    { icon: Mail, label: "Extracteur", path: "/dashboard" },
    { icon: HistoryIcon, label: "Historique", path: "/history" },
    ...(user?.is_admin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  return (
    <aside className="sidebar flex flex-col" data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-zinc-950 font-['Manrope']">OrderFlow</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <button
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md font-medium transition-colors ${
                  location.pathname === item.path
                    ? "bg-orange-50 text-orange-600"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-zinc-200">
        <div className="flex items-center gap-3 mb-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 bg-zinc-200 rounded-full flex items-center justify-center">
              <span className="text-zinc-600 font-medium">
                {user?.name?.charAt(0) || "U"}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Déconnexion
        </Button>
      </div>
    </aside>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-zinc-100 text-zinc-700",
    exported: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700",
  };

  const icons = {
    pending: Clock,
    exported: CheckCircle,
    failed: AlertCircle,
  };

  const labels = {
    pending: "En attente",
    exported: "Exportée",
    failed: "Échouée",
  };

  const Icon = icons[status] || Clock;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      <Icon className="w-3 h-3" />
      {labels[status] || status}
    </span>
  );
};

// Order Detail Modal
const OrderDetailModal = ({ order, isOpen, onClose, onExport, isExporting }) => {
  if (!order) return null;

  const data = order.extracted_data || {};

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope']">
            <FileSpreadsheet className="w-5 h-5 text-orange-500" />
            Commande {order.order_id?.slice(-8)}
          </DialogTitle>
          <DialogDescription>
            Créée le {new Date(order.created_at).toLocaleDateString("fr-FR", {
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            })}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between py-2 border-b border-zinc-100">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Statut</span>
              <StatusBadge status={order.status} />
            </div>

            {/* Client Info */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-zinc-900">Informations client</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-zinc-500">Nom:</span>
                  <span className="ml-2 font-mono text-zinc-800">{data.client_name || "—"}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Email:</span>
                  <span className="ml-2 font-mono text-zinc-800">{data.client_email || "—"}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Téléphone:</span>
                  <span className="ml-2 font-mono text-zinc-800">{data.client_phone || "—"}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Adresse:</span>
                  <span className="ml-2 font-mono text-zinc-800">{data.client_address || "—"}</span>
                </div>
              </div>
            </div>

            {/* Order Info */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-zinc-900">Détails commande</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-zinc-500">Référence:</span>
                  <span className="ml-2 font-mono text-zinc-800">{data.order_reference || "—"}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Date:</span>
                  <span className="ml-2 font-mono text-zinc-800">{data.order_date || "—"}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Montant:</span>
                  <span className="ml-2 font-mono text-orange-600 font-semibold">{data.total_amount || "—"}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Livraison:</span>
                  <span className="ml-2 font-mono text-zinc-800">{data.delivery_date || "—"}</span>
                </div>
              </div>
            </div>

            {/* Products */}
            {data.products && data.products.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-zinc-900">Produits</h4>
                <div className="bg-zinc-50 rounded-md overflow-hidden border border-zinc-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200">
                        <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Produit</th>
                        <th className="text-right p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Qté</th>
                        <th className="text-right p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Prix</th>
                        <th className="text-right p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Total</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {data.products.map((product, idx) => (
                        <tr key={idx} className="border-b border-zinc-100 last:border-0">
                          <td className="p-2 text-zinc-800">{product.name || "—"}</td>
                          <td className="p-2 text-right text-zinc-800">{product.quantity || "—"}</td>
                          <td className="p-2 text-right text-zinc-800">{product.unit_price || "—"}</td>
                          <td className="p-2 text-right text-zinc-800 font-medium">{product.total || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Notes */}
            {data.notes && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-zinc-900">Notes</h4>
                <p className="text-sm text-zinc-600 bg-zinc-50 p-3 rounded-md">{data.notes}</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button 
            onClick={() => onExport(order.order_id)}
            disabled={isExporting}
            className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Téléchargement...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Télécharger Excel
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function History() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await authFetch(`${API}/orders`, {
        });
        if (response.ok) {
          const data = await response.json();
          setOrders(data.orders || []);
        }
      } catch (error) {
        console.error("Failed to fetch orders:", error);
        toast.error("Erreur lors du chargement des commandes");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Export order to Excel
  const handleExport = async (orderId) => {
    setIsExporting(true);
    try {
      const response = await authFetch(`${API}/orders/${orderId}/excel`, {
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `commande_${orderId.slice(-8)}_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success("Fichier Excel téléchargé");
        // Update order status locally
        setOrders(orders.map(o => 
          o.order_id === orderId ? { ...o, status: "exported" } : o
        ));
        setSelectedOrder(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors du téléchargement");
      }
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setIsExporting(false);
    }
  };

  // Export all orders to Excel
  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const response = await authFetch(`${API}/orders/export/excel`, {
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `commandes_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success("Fichier Excel téléchargé");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors du téléchargement");
      }
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-50">
      <Sidebar />
      
      <main className="main-content flex-1" data-testid="history-page">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 font-['Manrope']">
              Historique des commandes
            </h1>
            <p className="text-zinc-600 mt-1">
              Consultez et gérez toutes vos commandes extraites
            </p>
          </div>
          {orders.length > 0 && (
            <Button
              onClick={handleExportAll}
              disabled={isExporting}
              className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
              data-testid="export-all-btn"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Tout exporter
            </Button>
          )}
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner" style={{ width: 40, height: 40 }}></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="empty-state py-20">
              <HistoryIcon className="empty-state-icon" />
              <p className="text-zinc-500 mb-2">Aucune commande</p>
              <p className="text-sm text-zinc-400">
                Les commandes extraites apparaîtront ici
              </p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Montant</th>
                  <th>Produits</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.order_id} data-testid={`order-row-${order.order_id}`}>
                    <td>
                      {new Date(order.created_at).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric"
                      })}
                    </td>
                    <td>{order.extracted_data?.client_name || "—"}</td>
                    <td className="text-orange-600 font-medium">
                      {order.extracted_data?.total_amount || "—"}
                    </td>
                    <td>
                      {order.extracted_data?.products?.length || 0} produit(s)
                    </td>
                    <td>
                      <StatusBadge status={order.status} />
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedOrder(order)}
                        className="gap-1"
                        data-testid={`view-order-${order.order_id}`}
                      >
                        <Eye className="w-4 h-4" />
                        Voir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Order Detail Modal */}
        <OrderDetailModal
          order={selectedOrder}
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onExport={handleExport}
          isExporting={isExporting}
        />
      </main>
    </div>
  );
}
